import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, Download, Loader2, RotateCcw, ShieldCheck, XCircle, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { downloadFile, errMsg, formatBytes, formatDate, useApi } from "@/lib/api";

const STATUS = {
  running: ["In corso", "bg-sky-50 text-sky-700"], ok: ["Completato", "bg-emerald-50 text-emerald-700"],
  partial: ["Parziale", "bg-amber-50 text-amber-700"], error: ["Fallito", "bg-red-50 text-red-700"], expired: ["Scaduto", "bg-slate-100 text-slate-500"],
};

function Verification({ v }) {
  if (!v) return <span className="text-xs text-slate-400">Non verificato</span>;
  if (v.status === "running") return <span className="text-xs text-sky-700 inline-flex items-center gap-1"><Loader2 size={13} className="animate-spin" />Verifica in corso…</span>;
  return v.ok
    ? <span className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={13} />Verificato {formatDate(v.at, true)} · {v.checked} progetti integri</span>
    : <span className="text-xs text-red-600 inline-flex items-center gap-1"><XCircle size={13} />Verifica fallita: {v.failed.length} progetti</span>;
}

function RunItems({ run, onRestored }) {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => { api.get(`/backups/${run.id}`).then(({ data }) => setItems(data.items)).catch((e) => toast.error(errMsg(e))); }, [api, run.id]);
  const restore = async (it, mode) => {
    const t = toast.loading("Ripristino in corso…");
    try {
      const { data } = await api.post(`/backups/${run.id}/restore`, { project_id: it.project_id, mode });
      toast.success(mode === "copy" ? `Creato "${data.name}"` : `"${data.name}" ripristinato (versione precedente salvata)`, { id: t });
      onRestored?.();
    } catch (e) { toast.error(errMsg(e), { id: t }); }
  };
  if (!items) return <p className="text-xs text-slate-500 py-2">Caricamento…</p>;
  return (
    <>
      <ul className="divide-y divide-slate-100 border-t border-slate-100 mt-3" data-testid={`backup-items-${run.id}`}>
        {items.map((it) => (
          <li key={it.project_id} className="py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm" data-testid={`backup-item-${it.project_id}`}>
            <div className="min-w-0 flex-1 basis-full sm:basis-0">
              <p className="font-medium text-slate-800 truncate">{it.name}{it.in_trash && <span className="ml-2 text-[11px] text-slate-400">(nel cestino)</span>}</p>
              <p className="text-xs text-slate-500">{it.pages} pagine · {it.sections} sezioni · {it.versions} versioni · {it.assets} asset · {formatBytes(it.size)} · sha256 {it.sha256.slice(0, 10)}…</p>
            </div>
            {run.status !== "expired" && (
              <div className="flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => restore(it, "copy")} data-testid={`backup-restore-copy-${it.project_id}`}><RotateCcw size={13} className="mr-1" />Ripristina come copia</Button>
                {!it.in_trash && <Button size="sm" variant="ghost" onClick={() => setConfirm(it)} data-testid={`backup-restore-overwrite-${it.project_id}`}>Sovrascrivi</Button>}
                <Button size="sm" variant="ghost" onClick={() => downloadFile(api, `/backups/${run.id}/items/${it.project_id}/download`, "backup.json.gz")} aria-label="Scarica" data-testid={`backup-download-${it.project_id}`}><Download size={14} /></Button>
              </div>
            )}
          </li>
        ))}
        {!items.length && <li className="py-2 text-xs text-slate-500">Nessun progetto in questo backup</li>}
      </ul>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} confirmLabel="Sovrascrivi"
        title={`Sovrascrivere "${confirm?.name}" con il backup del ${run.date}?`}
        description="Il contenuto attuale viene prima salvato come versione, quindi potrai sempre tornare indietro." onConfirm={() => { const it = confirm; setConfirm(null); restore(it, "overwrite"); }} />
    </>
  );
}

function RunRow({ run, onChanged }) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [label, cls] = STATUS[run.status] || STATUS.error;
  const verify = async () => {
    setVerifying(true);
    try {
      let { data } = await api.post(`/backups/${run.id}/verify`);
      for (let i = 0; data.status === "running" && i < 120; i++) { // large backups are verified in background
        await new Promise((r) => setTimeout(r, 2500));
        data = (await api.get(`/backups/${run.id}`)).data.verification || {};
      }
      if (data.status === "running") toast.message("Verifica ancora in corso: ricarica la pagina tra qualche minuto");
      else toast[data.ok ? "success" : "error"](data.ok ? "Backup integro" : "Verifica fallita");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
    setVerifying(false);
  };
  return (
    <li className="py-3" data-testid={`backup-run-${run.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-x-4 gap-y-2">
        <button onClick={() => setOpen(!open)} className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 flex-1 text-left" data-testid={`backup-run-toggle-${run.id}`}>
          <ChevronDown size={15} className={`shrink-0 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="font-medium text-slate-900 text-sm whitespace-nowrap">{formatDate(run.started_at, true)}</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}</span>
          <span className="text-xs text-slate-500 basis-full sm:basis-auto pl-6 sm:pl-0">{run.trigger} · {run.projects ?? "…"} progetti · {formatBytes(run.total_size || 0)}</span>
        </button>
        <div className="flex flex-wrap items-center gap-3 pl-6 lg:pl-0">
        <Verification v={run.verification} />
        {["ok", "partial"].includes(run.status) && (
          <Button size="sm" variant="outline" onClick={verify} disabled={verifying} data-testid={`backup-verify-${run.id}`}>
            {verifying ? <Loader2 size={13} className="mr-1 animate-spin" /> : <ShieldCheck size={13} className="mr-1" />}Verifica integrità
          </Button>
        )}
        </div>
      </div>
      {run.errors?.length > 0 && <p className="mt-1 ml-6 text-xs text-red-600">{run.errors.length} errori: {run.errors[0].error}</p>}
      {open && run.status !== "running" && <div className="ml-6"><RunItems run={run} onRestored={onChanged} /></div>}
    </li>
  );
}

export function BackupRuns({ onRestored }) {
  const api = useApi();
  const [runs, setRuns] = useState(null);
  const load = useCallback(() => api.get("/backups").then(({ data }) => setRuns(data)).catch(() => setRuns([])), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!runs?.some((r) => r.status === "running")) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [runs, load]);
  const runNow = async () => {
    try { await api.post("/backups/run"); toast.success("Backup avviato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Ogni notte alle 02:30 (ora italiana) tutti i progetti vengono salvati su Object Storage e conservati per 30 giorni.</p>
        <Button size="sm" onClick={runNow} className="bg-sky-600 hover:bg-sky-700" disabled={runs?.some((r) => r.status === "running")} data-testid="backup-run-now-btn"><Play size={13} className="mr-1.5" />Esegui backup ora</Button>
      </div>
      <ul className="mt-3 divide-y divide-slate-100" data-testid="backup-runs">
        {runs === null && <li className="py-3 text-sm text-slate-500">Caricamento…</li>}
        {runs?.length === 0 && <li className="py-3 text-sm text-slate-500" data-testid="backup-runs-empty">Nessun backup ancora eseguito</li>}
        {runs?.map((r) => <RunRow key={r.id} run={r} onChanged={() => { load(); onRestored?.(); }} />)}
      </ul>
    </div>
  );
}
